/* global PLATFORM, G */
import { v4 as uuidV4 } from 'uuid'
import { curry, deleteKey, setKey } from 'lib/util'
import { send, target as getTarget } from 'app/_shared/events/message'
import { changeStatus as changeTicketStatus } from 'app/_shared/events/status'
import { get as getAttachments } from 'app/_shared/events/attachment'
import uploadAttachments, { trackAttachments } from 'app/_shared/events/message/uploadAttachments'
import editStatusMessage from 'app/_shared/events/message/editStatusMessage'

/**
 * Event Handler Send Status Message
 *
 * Attempts to send a new message according to the `type`, `newStatus` and `text` properties passed
 * inside the event's detail.
 *
 * `newStatus` could either be
 * - a number, or
 * - an object.
 *
 * If it is a number (e.g. `newStatus: 80`) the parent's (ticket or request) status will be
 * set to it.
 *
 * If it's an object, it's possible to declare the newStatus based on the parent's current status.
 * A property with the parent's current status as a KEY, and its desired new status as its VALUE
 * is expected.
 *
 * @example
 * newStatus: {
 *    '80': 60
 * }
 *
 * indicates a `newStatus` of `60` if (and only if) the parent's current status is `80`.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component    the current action's main component
 * @param {Event} event                   information about the event that triggered this handler
 * @param {Object} event.detail           custom event's properties
 * @param {number} event.detail.newStatus the ticket/request status to be set.
 * @param {string} event.detail.text      the message's content
 * @param {string} event.detail.type      the message's type
 * @returns {Promise<void>}
 */
const sendStatusMessage = async (module, component, event) => {
  event?.preventDefault?.()
  event?.stopPropagation?.()

  const eventBus = module[G.ADAPTER][G.EVENTS]

  try {
    if (module[G.STATE]?.currentMessage) {
      await editStatusMessage(module, component, event)
      return
    }

    const { newStatus, newStatusReason, text: eventText, type } = event?.detail || event || {}
    const target = getTarget(module)

    const { text = eventText } = module[G.STATE]

    const model = module[G.MODEL]
    const { status } = model[G.CHILDREN]
    const currentStatus = status[G.CACHE]
    const { number: title } = model[G.CACHE].value
    const attachments = await getAttachments(module, component, event)
    const attachmentsCount = attachments.length

    // Set a temporary property to declare that we did this change
    model[G.STATE][G.UPDATE] = true;

    (typeof newStatus === 'object' && (currentStatus in newStatus))
      && await changeTicketStatus(module, component, newStatus[currentStatus], newStatusReason, false, type);
    (typeof newStatus === 'number')
      && await changeTicketStatus(module, component, newStatus, newStatusReason, false, type)

    if (!attachmentsCount && !text) return

    const key = uuidV4()

    // Show attachments in chat immediately by tracking them individually
    trackAttachments(module, component, attachments, {
      ...event,
      detail: { ...event?.detail, key },
    })

    const callback = async () => {
      try {
        deleteKey('text', module[G.STATE])
        deleteKey(G.UNDO, module[G.STATE][G.ACTION][G.STATE])

        // message created -> upload attachments in bg
        uploadAttachments(module, component, attachments, {
          ...event,
          detail: { ...event?.detail, key },
        })

        eventBus.dispatch(eventBus.type(G.MESSAGE, G.SCROLL))
        module[G.ADAPTER][G.UI].update(module)

        return true
      } catch (e) {
        console.error(e)
        return false
      }
    }

    await send(module, component, {
      data: { key, text, target, type, title },
      callback,
      ...attachmentsCount && { hasAttachments: attachmentsCount },
    })
  } catch (e) {
    console.error(e)
  }
}

export default curry(sendStatusMessage)
